import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlarmClock, IdCard, LifeBuoy, Timer } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchTickets, type AdminTicket } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A7.06 — Support tiketlari navbati. GET /admin/tickets (backend `650928d`).
 * Tiketlar userlardan keladi (POST /support/tickets — D-yo'li ulaydi). Ochiq
 * tiketlar tepada. Kategoriya/holat ustun-filtri.
 */
export function TicketQueue() {
  const t = useT()
  const navigate = useNavigate()

  const rowActions = useMemo<RowAction<AdminTicket>[]>(
    () => [
      {
        id: 'open',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/support/${r.id}`),
      },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminTicket>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'subject', header: t.support.subject, type: 'text', minWidth: 220 },
      { key: 'category', header: t.mod.category, type: 'text', width: 120 },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'open', label: t.support.open, status: 'open' },
          { value: 'pending', label: t.support.pending, status: 'pending' },
          { value: 'closed', label: t.support.closed, status: 'deleted' },
        ],
      },
      {
        key: 'user_id',
        header: t.mod.reporter,
        type: 'text',
        width: 140,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      { key: 'updated_at', header: t.support.updated, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchTickets, [])

  return (
    <>
      <PageHeader
        title={t.support.queue}
        screenId="A7.06"
        description={t.support.queueNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/support/sla"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Timer size={13} />
              {t.support.slaTitle}
            </Link>
            <Link
              to="/support/overdue"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <AlarmClock size={13} />
              {t.support.overdueTitle}
            </Link>
          </div>
        }
      />
      <DataTable<AdminTicket>
        screenId="A7.06"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <LifeBuoy size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
